import { Component } from "react"
import PropTypes from "prop-types"

export default class ConnectedServicesChart extends Component {
  getSettings(props) {
    const { servicesUrl } = props
    const totalUsers = props.worksWith.reduce((acc, curr) => acc + curr.users_with_enabled_applets, 0)
    const worksWith = props.worksWith.filter(ww => ww.users_with_enabled_applets / totalUsers > 0.01)

    return {
      chart: {
        animation: false,
        renderTo: this.chartContainer,
        type: "pie",
        events: {
          // Draw the per-slice service icons ourselves as SVG <image>s. Highcharts 13
          // renders useHTML data labels as in-SVG <foreignObject>s whose <body> inherits
          // IFTTT's global CSS — that mismeasures the labels, scattering the icons and
          // collapsing the pie's auto-size. Placing them at each slice's mid-angle avoids
          // foreignObject entirely.
          render() {
            ;(this.serviceIcons || []).forEach(icon => icon.destroy())

            const points = this.series[0]?.points || []
            this.serviceIcons = points
              .filter(point => point.percentage > 8 && point.shapeArgs)
              .map(point => {
                const { x, y, r, innerR, start, end } = point.shapeArgs
                const angle = (start + end) / 2
                const radius = (r + innerR) / 2
                const size = 30

                return this.renderer
                  .image(
                    point.icon,
                    this.plotLeft + x + Math.cos(angle) * radius - size / 2,
                    this.plotTop + y + Math.sin(angle) * radius - size / 2,
                    size,
                    size,
                  )
                  .attr({ zIndex: 6 })
                  .add()
              })
          },
        },
      },
      title: {
        text: this.renderTitle(),
        align: "center",
        verticalAlign: "middle",
        useHTML: true,
        y: 0,
        style: { maxWidth: "120px", whiteSpace: "normal", textAlign: "center" },
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          cursor: "default",
          dataLabels: {
            enabled: false,
          },
        },
        series: {
          animation: false,
          colorByPoint: true,
          events: {
            click(event) {
              if (servicesUrl) {
                window.location.assign(`${servicesUrl}/${event.point.options.moduleName}`)
              }
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        headerFormat: "",
        padding: 0,
        backgroundColor: "#fff",
        borderColor: "#fff",
        pointFormat: `<div class='tooltip'><div class='header' style='background-color: {point.color};'><img src='{point.icon}' alt='{point.name}' width='25' height='25'/>{point.name}</div><div class='row'><p class='stat'>{point.percent}%</p><p>of your connections are to this service</p></div><div class='row'></div></div>`,
      },
      series: [
        {
          name: "Connections",
          colorByPoint: true,
          colors: worksWith.map(ww => "#" + ww.works_with_channel.brand_color),
          innerSize: "50%",
          // Pin the size so the donut renders at a deterministic diameter.
          size: "95%",
          data: worksWith.map(ww => {
            return {
              y: ww.users_with_enabled_applets,
              name: ww.works_with_channel.name,
              icon: ww.works_with_channel.monochrome_image_url,
              color: `#${ww.works_with_channel.brand_color}`,
              percent: Math.ceil((ww.users_with_enabled_applets / totalUsers) * 100),
              moduleName: ww.works_with_channel.module_name,
            }
          }),
        },
      ],
    }
  }

  componentDidMount() {
    this.renderChart()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.worksWith !== this.props.worksWith ||
      prevProps.servicesUrl !== this.props.servicesUrl
    ) {
      this.renderChart()
    }
  }

  componentWillUnmount() {
    this.unmounted = true
    this.chart?.destroy()
  }

  async renderChart() {
    const Highcharts = await import("highcharts")
    // The dynamic import is async, so the component may have unmounted by now.
    if (this.unmounted) return

    Highcharts.setOptions(window.globalHighchartsOptions)
    this.chart?.destroy()
    this.chart = new Highcharts.Chart(this.getSettings(this.props))
  }

  renderTitle() {
    return `Top services connected`
  }

  render() {
    return <div className="donut-chart" ref={c => (this.chartContainer = c)} />
  }
}

ConnectedServicesChart.propTypes = {
  worksWith: PropTypes.array.isRequired,
  serviceName: PropTypes.string.isRequired,
  servicesUrl: PropTypes.string,
}
